import { Button } from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { useEffect, useRef, useState } from "react";

import { QuestionPanel } from "./QuestionPanel";
import { inboxItemContext, type InboxItem } from "./questionsClient";
import { useQuestionInbox } from "./questionsQueries";

export function QuestionsBubble() {
  const inbox = useQuestionInbox();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<InboxItem | null>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const count = inbox.data?.pages[0]?.total;
  const items = [
    ...new Map(
      inbox.data?.pages
        .flatMap((page) => page.items)
        .map((item) => [item.question.uuid, item]) ?? [],
    ).values(),
  ];
  items.sort((a, b) =>
    a.delivery.deadline_at.localeCompare(b.delivery.deadline_at),
  );
  useEffect(() => {
    if (open) close.current?.focus();
  }, [open]);
  const dismiss = () => {
    setOpen(false);
    toggle.current?.focus();
  };
  return (
    <>
      <section
        hidden={!open}
        aria-label="Questions from Brian"
        data-dd-privacy="hidden"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            dismiss();
          }
        }}
        className="pointer-events-auto w-full overflow-hidden rounded-sheet border border-rule bg-card shadow-sheet"
      >
        <header className="flex items-center justify-between border-b border-rule px-4 py-3">
          <h2 className="font-display text-lg">Questions from Brian</h2>
          <Button
            ref={close}
            size="sm"
            variant="ghost"
            onClick={dismiss}
            aria-label="Close questions"
          >
            Close
          </Button>
        </header>
        <div className="max-h-[min(34rem,calc(100dvh-16rem))] overflow-y-auto">
          {selected ? (
            <QuestionPanel
              key={selected.question.uuid}
              item={selected}
              visible={open}
              onBack={() => setSelected(null)}
            />
          ) : (
            <div className="p-4">
              {inbox.isError ? (
                <div role="alert">
                  <p>Questions could not be refreshed.</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void inbox.refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : inbox.isPending ? (
                <p role="status">Loading questions…</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-annotation">
                  No questions waiting for you.
                </p>
              ) : (
                <ul className="space-y-2">
                  {items.map((item) => (
                    <li key={item.question.uuid}>
                      <button
                        type="button"
                        className="w-full cursor-pointer rounded border border-rule p-3 text-left hover:bg-wall focus-visible:outline-2 focus-visible:outline-accent"
                        onClick={() => setSelected(item)}
                      >
                        <span className="block text-xs text-annotation">
                          {inboxItemContext(item)}
                        </span>
                        <span className="mt-1 block font-semibold">
                          {item.question.prompt}
                        </span>
                        <span className="mt-1 block text-xs text-annotation">
                          {item.delivery.reviewing_resolution
                            ? "Reviewing new evidence · "
                            : ""}
                          Needed by {formatDateTime(item.delivery.deadline_at)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {inbox.hasNextPage && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={inbox.isFetchingNextPage}
                  onClick={() => void inbox.fetchNextPage()}
                >
                  More questions
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
      {(count ?? 0) > 0 && (
        <button
          ref={toggle}
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="pointer-events-auto cursor-pointer rounded-full border border-rule bg-card px-4 py-2 text-sm font-semibold text-ink shadow-sheet focus-visible:outline-2 focus-visible:outline-accent"
        >
          {inbox.isError ? "Questions · unavailable" : `Questions · ${count}`}
        </button>
      )}
    </>
  );
}
